import { useState } from 'react';
import type { QuickActionDefinition } from '@neuroedge/shared';
import { Button, Modal, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useUpdateProfile } from '../../api/auth';

/**
 * Lets the logged-in user pick which Dashboard quick-action buttons they
 * see - a personal preference (same precedent as the theme-mode setting),
 * not a tenant-wide admin control. Only actions this user currently has
 * access to (`hasPermission` + `hasMenuAccess`) are offered - picking one
 * they can't use would just be a dead button, and if their access changes
 * later the Dashboard already re-filters on every render rather than
 * trusting a stale saved list.
 */
export function CustomizeQuickActionsModal({
  availableActions,
  selectedKeys,
  onClose,
}: {
  availableActions: QuickActionDefinition[];
  selectedKeys: Set<string>;
  onClose: () => void;
}) {
  const toast = useToast();
  const { refreshMe } = useAuth();
  const updateProfile = useUpdateProfile();
  const [selected, setSelected] = useState(selectedKeys);

  const groups = [...new Set(availableActions.map((a) => a.group))];

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    try {
      await updateProfile.mutateAsync({ dashboardQuickActions: [...selected] });
      await refreshMe();
      toast.success('Quick actions updated.');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save your quick actions.');
    }
  }

  async function resetToDefault() {
    try {
      await updateProfile.mutateAsync({ dashboardQuickActions: null });
      await refreshMe();
      toast.success('Reset to the default quick actions.');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reset your quick actions.');
    }
  }

  return (
    <Modal title="Customize quick actions" onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Pick which shortcut buttons show on your Dashboard. Only shown here: actions you currently
          have access to.
        </p>
        {groups.map((group) => (
          <fieldset key={group} style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
              }}
            >
              {group}
            </legend>
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              {availableActions
                .filter((a) => a.group === group)
                .map((a) => (
                  <label
                    key={a.key}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(a.key)}
                      onChange={() => toggle(a.key)}
                    />
                    {a.label}
                  </label>
                ))}
            </div>
          </fieldset>
        ))}
        {availableActions.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
            None of the quick-action shortcuts apply to your current access.
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button onClick={() => void save()} disabled={updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void resetToDefault()}
            disabled={updateProfile.isPending}
          >
            Reset to default
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
